/**
 * This file contains the UserCardView and associated sub-components.
 *
 * The "user card" is the large rectangular element that displayed at the top
 * of a user's profile page, and includes the avatar, username, bio,
 * and other public data.
 */

import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import classNames from "classnames";
import gql from "graphql-tag";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {HeadingLarge, LabelSmall} from "@khanacademy/wonder-blocks-typography";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {pureSmMin} from "@repo/shared-styles";
import {staticUrl} from "@repo/urls";

import AvatarCustomizerModal from "#avatar-customizer-package/avatar-customizer-modal.tsx";
import ApolloQuery from "#data-access/gql/apollo-query.tsx";
import {hideExternalLinks} from "#discussion-package/util.tsx";
import $ from "#jquery/jquery.ts";
import withBackboneModels from "#react-components-package/with-backbone-models.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import deprecatedReactRender from "#rendering/deprecated-react-render.tsx";
import type {ProfileModelType} from "#shared-package/profile-model.ts";

import {getAvatarDisplayName} from "./strings.ts";
import UserPointsAndBadges from "./user-points-and-badges.tsx";
import UsernamePicker from "./username-picker.tsx";

type ProfileInfoEditFields =
    typeof import("../profile-settings-package/profile-settings.tsx").default;

const scrollAmount = 90;

type UserCardAvatarViewProps = {
    /**
     * The avatar that this privacy option is associated with.
     */
    avatarName: string;
    avatarSrc: string;
    onClick: () => unknown;
};

const AVATAR_DATA_QUERY: GqlOperation<AvatarData, Variables> = gqlOp(gql`
    query avatarDataForProfile($kaid: String!) {
        user(kaid: $kaid) {
            id
            avatar {
                name
                imageSrc
            }
        }
    }
`);

const CHANGED_AVATAR_DATA_QUERY: GqlOperation<ChangedAvatarData, Variables> =
    gqlOp(gql`
        query hasChangedAvatar($kaid: String!) {
            user(kaid: $kaid) {
                id
                hasChangedAvatar
            }
        }
    `);

type AvatarData = {
    user?: {
        id: string;
        avatar: {
            name: string;
            imageSrc: string;
        };
    };
};
type ChangedAvatarData = {
    user?: {
        id: string;
        hasChangedAvatar: boolean;
    };
};
type Variables = {
    kaid: string;
};

/**
 * Renders the avatar in the user card
 */
class UserCardAvatarView extends React.Component<UserCardAvatarViewProps> {
    onKeyUpAvatar = (event: React.KeyboardEvent) => {
        if (event.key === "Enter" || event.key === " ") {
            this.props.onClick();
        }
    };

    render(): React.ReactNode {
        const {onClick, avatarSrc, avatarName} = this.props;
        const avatarIsEditable = !!onClick;

        const classes = classNames({
            "avatar-pic-container": true,
            editable: avatarIsEditable,
        });

        const avatarLabel = getAvatarDisplayName(avatarName);

        const altText = avatarIsEditable
            ? t`${avatarLabel} avatar. Click to change your avatar.`
            : t`${avatarLabel} avatar.`;

        return (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO: Address a11y error
            <div
                className={classes}
                style={{float: "left"}}
                onClick={onClick}
                role={avatarIsEditable ? "button" : ""}
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- TODO: Address a11y error
                tabIndex={avatarIsEditable ? 0 : undefined}
                onKeyUp={avatarIsEditable ? this.onKeyUpAvatar : undefined}
            >
                <div className="user-avatar-background">
                    <img
                        src={staticUrl(avatarSrc)}
                        className="avatar-pic"
                        alt={altText}
                    />
                </div>
            </div>
        );
    }
}

type UserCardBasicInfoViewProps = {
    /**
     * The `ProfileModel` instance that this privacy option is
     * associated with.
     */
    model: ProfileModelType;
    onClick: () => unknown;
};

/**
 * Renders the "basic info" in the user card (username, nickname/"real name",
 * and bio)
 */
class UserCardBasicInfoView extends React.Component<UserCardBasicInfoViewProps> {
    render(): React.ReactNode {
        const fullyEditable = this.props.model.isFullyEditable();

        // If a phantom has a proper "nickname" set, that means they're midway
        // through the signup process.
        // TODO(diedra): I don't think this is true anymore -
        // for phantoms without nicknames set,
        // `this.props.model.get("nickname")` returns "Learner".
        // See `get_default_nickname_for` in `nicknames.py`
        const showSignupButton =
            this.props.model.get("isPhantom") &&
            !this.props.model.get("nickname");
        let signupButtonEl = null;
        if (showSignupButton) {
            // @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'null'.
            signupButtonEl = (
                <div className="user-card-signup-button">
                    <a
                        href="/signup?continue=%2Fprofile"
                        className="simple-button green"
                    >
                        {t`Sign up to claim your profile`}
                    </a>
                </div>
            );
        }

        let usernameContents:
            | React.ReactElement<React.ComponentProps<"span">>
            | string = this.props.model.usernameFormatted();
        if (!usernameContents && fullyEditable) {
            // Hide widget on mobile (< 568px)
            usernameContents = (
                <span className="empty-field pure-hidden-xs">
                    {t`Pick a username`}
                </span>
            );
        }

        // for the profile header, we do an async retrieval of full profile
        // info, so leave bio blank until that returns
        const loadingProfileHeader = !this.props.model.get(
            "includesUserDataInfo",
        );

        const bioContentsFromProfile = this.props.model.get("bio");
        const bioContents = bioContentsFromProfile ? (
            hideExternalLinks(bioContentsFromProfile)
        ) : fullyEditable && !loadingProfileHeader ? (
            // Hide widget on mobile (< 568px)
            <span className="empty-field  pure-hidden-xs">
                {t`Add your bio`}
            </span>
        ) : undefined;

        const classes = classNames({
            "user-deets": true,
            editable: fullyEditable,
        });
        return (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- TODO: Address a11y error
            <div className={classes} onClick={this.props.onClick}>
                <View style={styles.nicknameContainer}>
                    <HeadingLarge tag="h2">
                        {this.props.model.get("nickname")}
                    </HeadingLarge>
                    <LabelSmall style={styles.nicknameSubcontents}>
                        <span>{usernameContents}</span>
                        {" - "}
                        <span>{bioContents}</span>
                    </LabelSmall>
                </View>
                {signupButtonEl}
            </div>
        );
    }
}

type UserCardViewProps = {
    /**
     * The `ProfileModel` instance that this privacy option is
     * associated with.
     */
    model: ProfileModelType;
    showBadgeSummary: boolean;
};

type UserCardViewState = {
    usernamePicker: any | null | undefined; // Backbone.View
    showAvatarCustomizer: boolean;
    // We use this flag to render some non-critical information only
    // on the client, and not on the server.
    initialRender: boolean;
};

/**
 * Renders the "user card", the large rectangular element that displayed at
 * the top of a user's profile page. Includes the avatar, username, bio,
 * other public data, and "Follow" button.
 */
class UserCardView extends React.Component<
    UserCardViewProps,
    UserCardViewState
> {
    // TODO(briangenisio): This is a hack to remove the deprecated call to
    // this.isMounted() but is still considered an anti-pattern.  Replace
    // with code which does not rely on the mounted state like Redux or GraphQL
    _isMounted: boolean;
    _isUsernamePickerLoading: boolean | undefined;
    _ProfileSettings: ProfileInfoEditFields | undefined;
    _unmountBasicInfo: (() => void) | undefined;
    _unmountPrivacyIndicator: (() => void) | undefined;

    static SCROLL_AMOUNT: number = scrollAmount;

    static defaultProps: {
        showBadgeSummary: boolean;
    } = {
        showBadgeSummary: true,
    };

    constructor(props: UserCardViewProps) {
        super(props);

        // TODO(stephanie): It would be nice to refactor avatarPicker and
        // usernamePicker into React/JSX someday, but not urgent. I'll leave
        // that for another wretched soul...
        // EDIT(david, Aug. 11): avatarPicker: check! avatar-customizer.jsx
        this.state = {
            usernamePicker: null,
            showAvatarCustomizer: false,
            // We use this flag to render some non-critical information only
            // on the client, and not on the server.
            initialRender: true,
        };
        this._isMounted = false;
    }

    componentDidMount() {
        this._isMounted = true;

        // Show the avatar customizer on query string match. This is so we can
        // have links from other pages or external sources open the avatar
        // customizer (e.g. emails or the nav bar notifications dropdown).
        //
        // NOTE(jordan): Hide the avatar customizer on mobile.
        // NOTE(boris): Hide avatar customizer if not on editable user.
        let showAvatarCustomizer = false;
        if (
            this.props.model &&
            this.props.model.isEditable() &&
            window.innerWidth > parseFloat(pureSmMin)
        ) {
            const params = new URLSearchParams(
                // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
                // eslint-disable-next-line no-restricted-syntax
                window.location.search,
            );
            showAvatarCustomizer = !!params.get("show_avatar_customizer");
        }

        this.setState({
            initialRender: false,
            showAvatarCustomizer,
        });
    }

    componentWillUnmount() {
        this._isMounted = false;
        const {usernamePicker} = this.state;

        const target = this._getBasicInfoEl();
        if (target && usernamePicker) {
            usernamePicker.remove();
        }
        this._unmountBasicInfo?.();
        this._unmountBasicInfo = undefined;
        this._unmountPrivacyIndicator?.();
        this._unmountPrivacyIndicator = undefined;
    }

    _onCustomizeAvatarClick: () => void = () => {
        this.setState({showAvatarCustomizer: true});
    };

    _onAvatarCustomizerClose: () => void = () => {
        this.setState({showAvatarCustomizer: false});
    };

    _getBasicInfoEl(): HTMLElement | null {
        return document.getElementById("profile-info-edit-fields");
    }

    _getPrivacyIndicatorEl(): null | HTMLElement {
        return document.getElementById("edit-profile-privacy-indicator");
    }

    _mountBio() {
        this._unmountBasicInfo?.();
        this._unmountBasicInfo = undefined;

        if (this._ProfileSettings) {
            const ProfileInfoEditFields = this._ProfileSettings;
            const basicInfoEl = this._getBasicInfoEl();
            if (basicInfoEl) {
                this._unmountBasicInfo = deprecatedReactRender(
                    <ProfileInfoEditFields profileModel={this.props.model} />,
                    basicInfoEl,
                );
            }
        }
    }

    onEditBasicInfoClick: () => void = () => {
        // Child accounts aren't "fully editable" and can't edit basic info.
        if (!this.props.model.isFullyEditable()) {
            return;
        }

        // Since we asynchronously load the UsernamePicker modal, we need to
        // make sure we don't try to load it twice or do anything strange while
        // it's still loading.
        if (this._isUsernamePickerLoading) {
            return;
        }

        if (this.state.usernamePicker) {
            this.state.usernamePicker.toggle(this.props.model);
            this._mountBio();
            return;
        }
        this._isUsernamePickerLoading = true;

        const {model} = this.props;
        const usernamePicker = new UsernamePicker({model});

        Promise.all([
            import("../profile-settings-package/profile-settings.tsx").then(
                (module) => module.default,
            ),
            import("../../stylesheets/shared-package/shared.less"),
            // @ts-expect-error - TS2345 - Argument of type '([ProfileSettings]: [any]) => void' is not assignable to parameter of type '(value: [ComponentClass<ExportProps, any> | FunctionComponent<ExportProps>, any]) => void | PromiseLike<void>'.
        ]).then(([ProfileSettings]: [any]) => {
            if (!this._isMounted) {
                return;
            }

            $("body").append(usernamePicker.render().el);

            this._ProfileSettings = ProfileSettings;

            this._mountBio();

            // Dummy indicator that all profile information is public
            const privacyIndicator = (
                <div>
                    <span className="icon-globe" />
                    <span>
                        {" "}
                        {t`Anyone can see your username, avatar, and bio.`}
                    </span>
                </div>
            );
            const privacyIndicatorEl = this._getPrivacyIndicatorEl();
            if (privacyIndicatorEl) {
                this._unmountPrivacyIndicator = deprecatedReactRender(
                    privacyIndicator,
                    privacyIndicatorEl,
                );
            }

            this.setState(
                {
                    usernamePicker: usernamePicker,
                },
                () => {
                    const {usernamePicker} = this.state;
                    if (usernamePicker) {
                        usernamePicker.toggle();
                    }
                    this._isUsernamePickerLoading = false;
                },
            );
        });
    };

    render(): React.ReactNode {
        let editMenuEl = null;
        if (this.props.model.isFullyEditable()) {
            // @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'null'.
            editMenuEl = (
                <Button
                    size="small"
                    onClick={this.onEditBasicInfoClick}
                    style={styles.editProfileButton}
                    kind="secondary"
                >
                    {t`Edit Profile`}
                </Button>
            );
        }

        // The user-card-privacy-indicator is rendered later, in profile-
        // widgets.jsx
        // TODO(stephanie): perhaps have that happen here instead someday

        let userPointsAndBadges;
        // TODO (jessie): show points and badges to the public based on the
        // user's privacy settings
        if (this.props.showBadgeSummary && !this.state.initialRender) {
            userPointsAndBadges = (
                <UserPointsAndBadges profileModel={this.props.model} />
            );
        }

        const basicInfo = (
            <div className="basic-user-info">
                <ApolloQuery
                    query={AVATAR_DATA_QUERY}
                    variables={{kaid: this.props.model.get("kaid")}}
                    fetchPolicy="cache-and-network"
                >
                    {({data}) => {
                        if (!data?.user) {
                            // TODO(csilvers): go back to using a View
                            // for `avatar-pic` once we've moved this
                            // component to use Aphrodite.  See
                            // https://github.com/Khan/webapp/pull/12821#issuecomment-1492029347
                            return (
                                <div
                                    className="user-info-container"
                                    style={{float: "left"}}
                                >
                                    <div className="avatar-pic" />
                                </div>
                            );
                        }
                        return (
                            <UserCardAvatarView
                                avatarName={data.user.avatar.name}
                                avatarSrc={data.user.avatar.imageSrc}
                                onClick={
                                    this.props.model.isEditable()
                                        ? this._onCustomizeAvatarClick
                                        : () => {}
                                }
                            />
                        );
                    }}
                </ApolloQuery>
                <UserCardBasicInfoView
                    model={this.props.model}
                    onClick={this.onEditBasicInfoClick}
                />
            </div>
        );

        $("#dashboard-profile-content").addClass("profile-header-present");

        // The old profile has user-info-container in a handlebars
        // template, but this makes the switch from the large profile
        // header to the small profile header more clear
        return (
            <div className="user-info-container large-header">
                {this.state.showAvatarCustomizer && (
                    <ApolloQuery
                        query={CHANGED_AVATAR_DATA_QUERY}
                        variables={{kaid: this.props.model.get("kaid")}}
                        fetchPolicy="cache-and-network"
                    >
                        {({loading, data}) => {
                            return (
                                <AvatarCustomizerModal
                                    onClose={this._onAvatarCustomizerClose}
                                    hasChangedAvatar={
                                        !loading && data?.user?.hasChangedAvatar
                                    }
                                />
                            );
                        }}
                    </ApolloQuery>
                )}
                <div className="user-info clearfix">
                    <div className="user-info-all">
                        {basicInfo}
                        {/* Hide button on < 568px */}
                        <div className="user-card-edit-menu pure-hidden-xs">
                            {editMenuEl}
                        </div>
                        {userPointsAndBadges}
                    </div>
                </div>
            </div>
        );
    }
}

const styles = StyleSheet.create({
    nicknameContainer: {
        color: color.offBlack,
        marginTop: 16,
    },
    nicknameSubcontents: {
        marginTop: 2,
    },
    editProfileButton: {
        minWidth: 125,
        marginTop: 24,
    },
});

export default withBackboneModels((props: UserCardViewProps) => [props.model])(
    UserCardView,
) as React.ComponentType<
    JSX.LibraryManagedAttributes<
        typeof UserCardView,
        React.ComponentProps<typeof UserCardView>
    >
>;
